import React, { Suspense, lazy } from "react";
import { Routes, Route, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { RootState } from "./store";

const HomePage = lazy(() => import("./pages/HomePage"));
const NotFoundPage = lazy(() => import("./pages/NotFoundPage"));
const ProductsPage = lazy(() => import("./pages/ProductsPage"));
const AboutPage = lazy(() => import("./pages/AboutPage"));
const PosPage = lazy(() => import("./pages/AboutPage/PosTerminalPage"));
const ApiPage = lazy(() => import("./pages/AboutPage/ApiPage"));
const TermsPage = lazy(() => import("./pages/TermsPage"));
const PrivacyPage = lazy(() => import("./pages/PrivacyPage"));
const ContactUsPage = lazy(() => import("./pages/ContactUsPage"));
const CareersPage = lazy(() => import("./pages/CareersPage"));
const GamePage = lazy(() => import("./pages/ProductPage/Game"));
const CreditPage = lazy(() => import("./pages/ProductPage/Credit"));
const LoginPage = lazy(() => import("./pages/LoginPage/LoginPage"));
const RegisterPage = lazy(() => import("./pages/LoginPage/RegisterPage"));
const CartPage = lazy(() => import("./pages/CartPage"));
const UserPage = lazy(() => import("./pages/UserSettingsPage"));
import ProtectedRoute from "./ProtectedRoutes";
const OfflinePage = lazy(() => import("./pages/OfflinePage/OfflinePage"));
const UnderConstructionPage = lazy(() => import("./pages/UnderConstruction"));
const WelcomePage = lazy(() => import("./pages/WelcomePage/WelcomePage"));
import ReferralTracker from "components/ReferralTracker";
const AdyenHarness =
  import.meta.env.MODE !== "production"
    ? lazy(() => import("./pages/CartPage/providers/__tests__/AdyenHarness"))
    : undefined;

import { useTheme } from "@mui/system";
import { Box, alpha } from "@mui/material";
import { useFetchHealthQuery } from "store/RTK/healthApi";
import { useFetchSettingsQuery as useFetchShopSettingsQuery } from "store/RTK/optionsApi";

const RoutesGroup = () => {
  const user = useSelector((state: RootState) => state.auth.user);
  const isEmailVerified = useSelector(
    (state: RootState) => state.auth.isEmailVerified,
  );
  const hasMissingInfo = useSelector(
    (state: RootState) => state.auth.hasMissingInfo,
  );
  const identityRequireCheck = useSelector(
    (state: RootState) => state.auth.identityRequireCheck,
  );
  const welcomePageLoading = useSelector(
    (state: RootState) => state.auth.welcomePageLoading,
  );

  const theme = useTheme();
  const location = useLocation();
  const isOnline = useSelector((state: RootState) => state.auth.isOnline);
  // Backend health polling (every 15s). If backend unreachable, show Under Construction.
  const { data: health, isError: isHealthError, isLoading: isHealthLoading } = useFetchHealthQuery(undefined, {
    pollingInterval: 15000,
    refetchOnReconnect: true,
    refetchOnFocus: true,
  });
  const isBackendHealthy = isHealthLoading || (!isHealthError && health?.status === "ok");
  const { data: shopSettings } = useFetchShopSettingsQuery();
  const isUnderConstruction = Boolean((shopSettings as any)?.underConstruction);
  const isRestricted =
    !isEmailVerified ||
    hasMissingInfo ||
    identityRequireCheck ||
    welcomePageLoading;

  return (
    <Box
      sx={{
        width: "100%",
        backgroundImage:
          theme.palette.mode === "light"
            ? "linear-gradient(180deg, #c9e3fe, #FFF)"
            : `linear-gradient(#02294F, ${alpha("#090E10", 0.0)})`,
        backgroundSize: "100% 200px",
        backgroundRepeat: "no-repeat",
      }}
    >
      <ReferralTracker />
      <Suspense fallback={<div style={{ padding: 24 }}>Loading…</div>}>
        <Routes location={location}>
        {isRestricted && user ? (
          <Route
            path="*"
            element={
              <ProtectedRoute user={user}>
                <WelcomePage />
              </ProtectedRoute>
            }
          />
        ) : (
          <>
            {isOnline ? (
              <>
                {!isBackendHealthy || isUnderConstruction ? (
                  <>
                    <Route path="*" element={<UnderConstructionPage />} />
                  </>
                ) : (
                  <>
                    <Route path="/__uc" element={<UnderConstructionPage />} />
                    <Route path="/" element={<HomePage />} />
                    <Route path="*" element={<NotFoundPage />} />
                    <Route path="/about" element={<AboutPage />} />
                    <Route path="/about/pos" element={<PosPage />} />
                    <Route path="/about/api" element={<ApiPage />} />
                    <Route path="/terms" element={<TermsPage />} />
                    <Route path="/login" element={<LoginPage />} />
                    <Route path="/register" element={<RegisterPage />} />
                    <Route path="/privacy" element={<PrivacyPage />} />
                    <Route path="/contactus" element={<ContactUsPage />} />
                    <Route path="/Careers" element={<CareersPage />} />

                    <Route
                      path="/dashboard/*"
                      element={
                        <ProtectedRoute user={user}>
                          <UserPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route path="/cart/*" element={<CartPage />} />
                    {AdyenHarness ? (
                      <Route path="/__tests__/adyen" element={<AdyenHarness />} />
                    ) : null}
                    <Route path="/products" element={<ProductsPage />} />
                    <Route
                      path="game/:categoryId/:platformName/:productName"
                      element={<GamePage key={window.location.pathname} />}
                    />
                    <Route
                      path="dlc/:categoryId/:platformName/:productName"
                      element={<GamePage key={window.location.pathname} />}
                    />
                    <Route
                      path="gift-card/:categoryId/:platformName/:productName/:editionName"
                      element={<CreditPage key={window.location.pathname} />}
                    />
                    <Route
                      path="subscription/:categoryId/:platformName/:productName/:editionName"
                      element={<CreditPage key={window.location.pathname} />}
                    />
                  </>
                )}
              </>
            ) : (
              <>
                <Route path="/about" element={<AboutPage />} />
                <Route path="/about/pos" element={<PosPage />} />
                <Route path="/about/api" element={<ApiPage />} />
                <Route path="/terms" element={<TermsPage />} />
                <Route path="/privacy" element={<PrivacyPage />} />
                <Route path="/careers" element={<CareersPage />} />
                <Route path="*" element={<OfflinePage />} />
              </>
            )}
          </>
        )}
        </Routes>
      </Suspense>
    </Box>
  );
};

export default RoutesGroup;
